import {
  handleIncomingErrorCode,
  queueCommands,
  resetCommandPipeline,
  tryResolvePendingCommand,
  type CommandQueueState,
} from "lib/deviceDrivers/sessionFramework/protocol/commandQueue";
import {
  IRxTransport,
  ITxTransport,
} from "lib/deviceDrivers/sessionFramework/device/types";
import {
  processLiveScpFrame,
  safeEmit,
} from "lib/deviceDrivers/truTest/scp/device/liveFrame";
import {
  resetSessionRecordCursorCommand,
  syncHardwareConfigCommands,
} from "./commands";
import {
  buildImportedScans,
  createDownloadState,
  DownloadMachineResultType,
  reduceDownloadFrame,
} from "./savedScansStateMachine";
import { tryParseEidOutputFormat } from "lib/deviceDrivers/truTest/scp/device/eid";
import { parseScpFrame } from "lib/deviceDrivers/truTest/scp/device/frames";
import { createScpCommandQueue } from "lib/deviceDrivers/truTest/scp/device/commands";
import {
  EIDOutputFormat,
  FrameType,
  type ScpFrame,
} from "lib/deviceDrivers/truTest/scp/device/types";
import {
  DeviceMode,
  DownloadState,
  type Xrs2iDeviceEventHandler,
  type Xrs2iDeviceHandle,
  Xrs2iEventType,
} from "./types";

const CANCEL_IGNORE_WINDOW_MS = 500;
const CANCEL_LIVE_SCAN_GUARD_WINDOW_MS = 3000;

interface Xrs2iState {
  mode: DeviceMode;
  eidOutputFormat?: EIDOutputFormat;
  downloadState?: DownloadState;
  // Ignore late frames for a short period after cancellation.
  ignoreIncomingUntil: number;
  // Suppress live-scan emission briefly after cancellation.
  suppressLiveScansUntil: number;
}

/**
 * Creates a live XRS2i protocol device for one transport pair.
 * The returned handle owns command sequencing, retries, saved-session import, and teardown for that scanner.
 */
export function createXrs2iDevice(
  tx: ITxTransport,
  rx: IRxTransport,
  onEvent: Xrs2iDeviceEventHandler,
): Xrs2iDeviceHandle {
  const queue = createScpCommandQueue();
  const xrs2i: Xrs2iState = {
    mode: DeviceMode.IDLE,
    ignoreIncomingUntil: 0,
    suppressLiveScansUntil: 0,
  };

  function onFatalFailure(code: string): void {
    safeEmit(onEvent, { type: Xrs2iEventType.ERROR, code });
    if (xrs2i.mode === DeviceMode.DOWNLOADING_SAVED_SCANS) {
      finishDownload(xrs2i, false, onEvent);
    } else {
      xrs2i.mode = DeviceMode.IDLE;
    }
  }

  const unsubscribe = rx.onReceive(payload => {
    receive(queue, xrs2i, tx, payload, onEvent, onFatalFailure);
  });

  return {
    async resetHardwareConfig() {
      prepareForFormatRequest(queue, xrs2i);
      queueCommands(queue, tx, syncHardwareConfigCommands(), onFatalFailure);
    },

    async readSavedScans() {
      resetCommandPipeline(queue);
      xrs2i.mode = DeviceMode.DOWNLOADING_SAVED_SCANS;
      xrs2i.downloadState = createDownloadState();
      xrs2i.ignoreIncomingUntil = 0;
      xrs2i.suppressLiveScansUntil = 0;
      queueCommands(
        queue,
        tx,
        [resetSessionRecordCursorCommand()],
        onFatalFailure,
      );
    },

    cancelReadSavedScans() {
      resetCommandPipeline(queue);
      xrs2i.mode = DeviceMode.IDLE;
      xrs2i.downloadState = undefined;
      xrs2i.ignoreIncomingUntil = Date.now() + CANCEL_IGNORE_WINDOW_MS;
      xrs2i.suppressLiveScansUntil =
        Date.now() + CANCEL_LIVE_SCAN_GUARD_WINDOW_MS;
    },

    destroy() {
      unsubscribe();
      resetCommandPipeline(queue);
      xrs2i.downloadState = undefined;
      xrs2i.mode = DeviceMode.IDLE;
      xrs2i.suppressLiveScansUntil = 0;
    },
  };
}

function prepareForFormatRequest(
  queue: CommandQueueState<ScpFrame>,
  xrs2i: Xrs2iState,
): void {
  // Reset the queue before asking for the format so stale download work cannot race the hardware sync request.
  resetCommandPipeline(queue);
  xrs2i.downloadState = undefined;
  xrs2i.mode = DeviceMode.AWAITING_EID_OUTPUT_FORMAT;
  xrs2i.ignoreIncomingUntil = 0;
  xrs2i.suppressLiveScansUntil = 0;
}

/**
 * Handles one raw payload from the scanner and routes it through the current protocol mode.
 * The order matters because ACKs, errors, saved-session frames, and live scan payloads all share the same transport.
 */
function receive(
  queue: CommandQueueState<ScpFrame>,
  xrs2i: Xrs2iState,
  tx: ITxTransport,
  serializedPayload: string,
  onEvent: Xrs2iDeviceEventHandler,
  onFatalFailure: (code: string) => void,
): void {
  if (
    xrs2i.ignoreIncomingUntil > 0 &&
    Date.now() >= xrs2i.ignoreIncomingUntil
  ) {
    xrs2i.ignoreIncomingUntil = 0;
  }

  if (xrs2i.ignoreIncomingUntil > 0) {
    return;
  }

  if (
    xrs2i.suppressLiveScansUntil > 0 &&
    Date.now() >= xrs2i.suppressLiveScansUntil
  ) {
    xrs2i.suppressLiveScansUntil = 0;
  }

  if (xrs2i.suppressLiveScansUntil > 0) {
    // Intentionally drops all frames (including ACKs/errors) — after cancellation
    // there is no pending command to resolve and any late protocol frames are noise.
    return;
  }

  const frame = parseScpFrame(serializedPayload);

  if (frame.type === FrameType.ERROR) {
    handleIncomingErrorCode(queue, tx, frame.code, onFatalFailure);
    return;
  }

  tryResolvePendingCommand(queue, tx, frame, onFatalFailure);

  if (
    xrs2i.mode === DeviceMode.DOWNLOADING_SAVED_SCANS &&
    xrs2i.downloadState
  ) {
    onDownloadFrame(queue, xrs2i, tx, frame, onEvent, onFatalFailure);
    return;
  }

  if (frame.type === FrameType.BRACKET) {
    const detectedFormat = tryParseEidOutputFormat(frame.raw);
    if (detectedFormat !== undefined) {
      xrs2i.eidOutputFormat = detectedFormat;
      if (xrs2i.mode === DeviceMode.AWAITING_EID_OUTPUT_FORMAT) {
        xrs2i.mode = DeviceMode.IDLE;
      }
      return;
    }
  }

  if (xrs2i.mode === DeviceMode.AWAITING_EID_OUTPUT_FORMAT) {
    xrs2i.mode = DeviceMode.IDLE;
  }

  processLiveScpFrame(
    frame,
    xrs2i.eidOutputFormat,
    eid =>
      safeEmit(onEvent, {
        type: Xrs2iEventType.SCAN,
        eid,
        created: new Date().toISOString(),
      }),
    () => safeEmit(onEvent, { type: Xrs2iEventType.INVALID_EID }),
  );
}

/**
 * Applies one scanner frame to the saved-session import state machine.
 * This keeps the import sequence linear so each ACK or payload advances the next command only when the device is ready.
 */
function onDownloadFrame(
  queue: CommandQueueState<ScpFrame>,
  xrs2i: Xrs2iState,
  tx: ITxTransport,
  frame: ScpFrame,
  onEvent: Xrs2iDeviceEventHandler,
  onFatalFailure: (code: string) => void,
): void {
  const { downloadState } = xrs2i;
  if (!downloadState) {
    finishDownload(xrs2i, false, onEvent);
    return;
  }

  const result = reduceDownloadFrame(
    downloadState,
    frame,
    xrs2i.eidOutputFormat,
  );

  for (const event of result.events) {
    safeEmit(onEvent, event);
  }

  if (result.type === DownloadMachineResultType.COMPLETE) {
    resetCommandPipeline(queue);
    finishDownload(xrs2i, result.success, onEvent);
    return;
  }

  queueCommands(queue, tx, result.commands, onFatalFailure);
}

/**
 * Finishes the saved-session import and emits the final progress summary.
 * Successful runs include the accumulated session scans, while failed runs only report counts and completion state.
 */
function finishDownload(
  xrs2i: Xrs2iState,
  success: boolean,
  onEvent: Xrs2iDeviceEventHandler,
): void {
  const { downloadState } = xrs2i;
  const completedSessions = downloadState?.completedSessionCount ?? 0;
  const totalSessions = downloadState?.totalSessionCount ?? null;
  const importedScans =
    success && downloadState ? buildImportedScans(downloadState) : {};

  // Reset the import state before emitting completion so consumers see the device as idle during the callback.
  xrs2i.downloadState = undefined;
  xrs2i.mode = DeviceMode.IDLE;

  safeEmit(onEvent, {
    type: Xrs2iEventType.SAVED_SCANS_COMPLETE,
    success,
    completedSessions,
    totalSessions,
    importedScans,
  });
}
